import { useState } from 'react';
import { joinMentiSession } from '../../../services/mentiParticipantService';

/**
 * Join screen for Mikkel Menti participants.
 * Two-step flow: PIN -> Name -> Join
 *
 * @param {Object} props
 * @param {function} props.onJoin - Callback with { sessionId, participantId }
 * @param {string} [props.prefilledPin] - Pre-filled PIN from URL param
 */
export default function MentiJoinScreen({ onJoin, prefilledPin = '' }) {
  const isValidPin = prefilledPin && /^[0-9]{6}$/.test(prefilledPin);
  const [step, setStep] = useState(isValidPin ? 'name' : 'pin');
  const [pin, setPin] = useState(isValidPin ? prefilledPin : '');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 6) {
      setPin(value);
      setError('');
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 30) {
      setName(value);
      setError('');
    }
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (!/^[0-9]{6}$/.test(pin.trim())) {
      setError('PIN må vere nøyaktig 6 siffer');
      return;
    }
    setStep('name');
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 30) {
      setError('Namn må vere mellom 2 og 30 teikn');
      return;
    }

    setLoading(true);

    try {
      const result = await joinMentiSession(pin, trimmed);
      onJoin({ sessionId: pin, participantId: result.id });
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Fann ikkje')) {
        setError('Fann ikkje Menti-økta. Sjekk PIN-koden og prøv igjen.');
      } else if (msg.includes('avslutta')) {
        setError('Denne Menti-økta er allereie avslutta.');
      } else {
        setError('Klarte ikkje bli med. Prøv igjen.');
      }
      setLoading(false);
    }
  };

  // Step 1: PIN input
  if (step === 'pin') {
    return (
      <div className="relative min-h-screen w-full flex flex-col items-center justify-between overflow-hidden bg-gradient-to-br from-blue-600 to-blue-700 font-display">
        {/* Decorative blobs */}
        <div className="absolute -top-6 left-[10%] w-14 h-14 bg-white/15 rounded-full blur-xl" />
        <div className="absolute top-[22%] right-[6%] w-16 h-16 bg-blue-300/25 rounded-full blur-xl" />
        <div className="absolute bottom-[28%] left-[4%] w-20 h-20 bg-blue-300/15 rounded-full blur-2xl" />

        {/* Header */}
        <header className="w-full flex flex-col items-center justify-center pt-16 pb-8 px-4 flex-1">
          <span className="material-symbols-outlined text-white/80 text-5xl mb-3">slideshow</span>
          <h1 className="text-white tracking-tight text-4xl md:text-5xl font-extrabold text-center drop-shadow-sm">
            Bli med i Mikkel Menti
          </h1>
          <p className="text-white/70 text-lg mt-2 text-center">Skriv inn PIN-koden frå skjermen</p>
        </header>

        {/* PIN form */}
        <main className="w-full max-w-[480px] px-6 pb-8 flex-1 flex flex-col justify-start items-center space-y-4">
          <div className="w-full bg-white/10 backdrop-blur-sm p-6 rounded-3xl shadow-2xl border border-white/15">
            <form className="flex flex-col gap-4 w-full" onSubmit={handlePinSubmit}>
              <label className="sr-only" htmlFor="menti-pin">PIN-kode</label>
              <input
                type="text"
                id="menti-pin"
                inputMode="numeric"
                value={pin}
                onChange={handlePinChange}
                placeholder="PIN-kode"
                className="block w-full text-center bg-white text-gray-900 placeholder:text-gray-400 rounded-full border-0 focus:ring-4 focus:ring-white/30 h-16 text-2xl font-bold shadow-inner transition-all duration-200 tracking-widest"
                disabled={loading}
                autoComplete="off"
                autoFocus
              />

              {error && (
                <div className="text-white bg-red-500/25 border border-red-400/30 px-4 py-3 rounded-xl text-center font-medium">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full h-14 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group"
              >
                <span>Gå vidare</span>
                <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">arrow_forward</span>
              </button>
            </form>
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full py-6 px-4 text-center">
          <p className="text-white/60 text-sm font-medium">
            Mikkel Kviss &mdash; Menti
          </p>
        </footer>
      </div>
    );
  }

  // Step 2: Name input
  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-between overflow-hidden bg-gradient-to-br from-blue-600 to-blue-700 font-display">
      <div className="absolute -top-6 left-[10%] w-14 h-14 bg-white/15 rounded-full blur-xl" />
      <div className="absolute top-[22%] right-[6%] w-16 h-16 bg-blue-300/25 rounded-full blur-xl" />

      <header className="w-full flex flex-col items-center justify-center pt-16 pb-8 px-4 flex-1">
        <span className="material-symbols-outlined text-white/80 text-5xl mb-3">person</span>
        <h1 className="text-white tracking-tight text-4xl md:text-5xl font-extrabold text-center drop-shadow-sm">
          Nesten klar!
        </h1>
        <p className="text-white/70 text-lg mt-2 text-center">Kva heiter du?</p>
      </header>

      <main className="w-full max-w-[480px] px-6 pb-8 flex-1 flex flex-col justify-start items-center space-y-4">
        <div className="w-full bg-white/10 backdrop-blur-sm p-6 rounded-3xl shadow-2xl border border-white/15">
          <form className="flex flex-col gap-4 w-full" onSubmit={handleNameSubmit}>
            <label className="sr-only" htmlFor="menti-name">Namn</label>
            <input
              type="text"
              id="menti-name"
              value={name}
              onChange={handleNameChange}
              placeholder="Ditt namn"
              className="block w-full text-center bg-white text-gray-900 placeholder:text-gray-400 rounded-full border-0 focus:ring-4 focus:ring-white/30 h-16 text-2xl font-bold shadow-inner transition-all duration-200"
              disabled={loading}
              autoComplete="off"
              autoFocus
            />

            {error && (
              <div className="text-white bg-red-500/25 border border-red-400/30 px-4 py-3 rounded-xl text-center font-medium">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-50"
            >
              <span>{loading ? 'Koplar til...' : 'Bli med'}</span>
              {!loading && (
                <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">login</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('pin');
                setError('');
              }}
              className="text-white/60 hover:text-white text-sm font-medium transition-colors"
            >
              &larr; Tilbake til PIN
            </button>
          </form>
        </div>
      </main>

      <footer className="w-full py-6 px-4 text-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 inline-flex items-center gap-2">
          <span className="text-white/70 text-sm">Menti-økt</span>
          <span className="text-white font-bold">{pin}</span>
        </div>
      </footer>
    </div>
  );
}
